import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { FontPreview } from '../components/FontPreview';
import { ShareButtons } from '../components/ShareButtons';
import { CollectionManager } from '../components/CollectionManager';
import { SacredGenerationOverlay } from '../components/SacredGenerationOverlay';
import { ScriptureInsight, downloadScripturePdf } from '../components/ScriptureInsight';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Loader2, RefreshCw, Type, Info, Download, Image as ImageIcon, Lock, LogIn, CreditCard } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typefaceCategories = [
  { 
    id: 'none', 
    name: 'Auto Detect', 
    description: 'Let AI choose the best category',
    icon: '✨'
  },
  { 
    id: 'sans_serif', 
    name: 'Sans Serif', 
    description: 'Clean, modern fonts without decorative strokes',
    icon: 'Aa',
    examples: 'Helvetica, Futura, Gotham'
  },
  { 
    id: 'serif', 
    name: 'Serif', 
    description: 'Traditional fonts with decorative strokes',
    icon: 'Aa',
    examples: 'Garamond, Baskerville, Playfair'
  },
  { 
    id: 'semi_serif', 
    name: 'Semi-Serif', 
    description: 'Hybrid blending serif and sans-serif',
    icon: 'Aa',
    examples: 'Optima, Scala Sans'
  },
  { 
    id: 'monospace', 
    name: 'Monospace', 
    description: 'Fixed-width technical fonts',
    icon: '</>',
    examples: 'Fira Code, JetBrains Mono'
  },
  { 
    id: 'display', 
    name: 'Display', 
    description: 'Bold fonts for headlines',
    icon: 'A!',
    examples: 'Impact, Bebas Neue, Cooper Black'
  },
  { 
    id: 'script', 
    name: 'Script', 
    description: 'Handwriting and calligraphy styles',
    icon: '✍',
    examples: 'Pacifico, Great Vibes'
  },
  { 
    id: 'blackletter', 
    name: 'Blackletter', 
    description: 'Gothic medieval calligraphic styles',
    icon: '𝔄',
    examples: 'Fraktur, Old English'
  },
  { 
    id: 'art_deco', 
    name: 'Art Deco', 
    description: '1920s glamorous geometric style',
    icon: '◆',
    examples: 'Broadway, Poiret One'
  },
  { 
    id: 'wacky', 
    name: 'Wacky', 
    description: 'Fun, playful, and quirky fonts',
    icon: '🎪',
    examples: 'Bangers, Bungee, Fredoka'
  },
];

const inspiringQuotes = [
  "The only way to do great work is to love what you do.",
  "Creativity takes courage.",
  "Design is intelligence made visible.",
  "Every artist was first an amateur.",
  "The details are not the details. They make the design.",
  "Simplicity is the ultimate sophistication.",
  "Colors, like features, follow the changes of emotions.",
  "Art is not what you see, but what you make others see.",
  "Typography is the craft of endowing human language with visual form.",
  "Good design is obvious. Great design is transparent.",
  "In the middle of difficulty lies opportunity.",
  "Imagination is the beginning of creation.",
];

const borderStyles = [
  { value: 'none', label: 'No Border' },
  { value: 'cathedral', label: 'Cathedral Arch', description: 'Gothic pointed arches with stone tracery' },
  { value: 'illuminated', label: 'Illuminated Manuscript', description: 'Medieval book page borders with gold leaf corners' },
  { value: 'ironcross', label: 'Iron Cross', description: 'Wrought iron cross motifs and Celtic knots' },
  { value: 'gargoyle', label: 'Gargoyle Frame', description: 'Stone gargoyle corner pieces with Gothic spires' },
  { value: 'rosewindow', label: 'Rose Window', description: 'Stained glass circular rose window pattern' },
  { value: 'cryptvines', label: 'Crypt Vines', description: 'Dark twisted vines with thorns and Gothic flowers' },
  { value: 'sacredscript', label: 'Sacred Script', description: 'Ancient runic borders with religious symbols' },
];

const letterSpaceOptions = [
  { value: '0px', label: '0px' },
  { value: '1px', label: '1px' },
  { value: '2px', label: '2px' },
  { value: '3px', label: '3px' },
  { value: '4px', label: '4px' },
  { value: '5px', label: '5px' },
  { value: '6px', label: '6px' },
  { value: '7px', label: '7px' },
  { value: '8px', label: '8px' },
  { value: '9px', label: '9px' },
  { value: '10px', label: '10px' },
];

const decorationOptions = [
  { value: 'none', label: 'None' },
  { value: 'underline', label: 'Underline' },
  { value: 'line-through', label: 'Line-through' },
  { value: 'overline', label: 'Overline' },
];

const decorationStyleOptions = [
  { value: 'solid', label: 'Solid' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'wavy', label: 'Wavy' },
];

export default function GeneratorPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const [prompt, setPrompt] = useState('');
  const [typefaceCategory, setTypefaceCategory] = useState('none');
  const [letterSpace, setLetterSpace] = useState('0px');
  const [decoration, setDecoration] = useState('none');
  const [decorationStyle, setDecorationStyle] = useState('solid');
  const [previewText, setPreviewText] = useState('');
  const [borderStyle, setBorderStyle] = useState('none');
  const [borderColor, setBorderColor] = useState('#0A0A0A');
  const [generatedFont, setGeneratedFont] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasingPreview, setPurchasingPreview] = useState(null);
  const [showCategoryInfo, setShowCategoryInfo] = useState(false);
  const [collections, setCollections] = useState([]);
  
  // Generation limits state
  const [generationLimits, setGenerationLimits] = useState(null);
  const [localGenerationCount, setLocalGenerationCount] = useState(() => {
    // Get anonymous generation count from localStorage
    const stored = localStorage.getItem('funtzguntz-anon-generations');
    return stored ? parseInt(stored, 10) : 0;
  });
  
  // Fetch generation limits on mount and when auth changes
  useEffect(() => {
    fetchGenerationLimits();
  }, [isAuthenticated, token]);
  
  const needsSignup = !isAuthenticated && (generationLimits?.needs_signup || localGenerationCount >= 3);
  const needsCredits = isAuthenticated && !!generationLimits?.needs_credits;

  const fetchGenerationLimits = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/generation/limits`, { headers });
      setGenerationLimits(response.data);
    } catch (error) {
      console.error('Failed to fetch generation limits:', error);
    }
  };
  
  // Pre-fill prompt from navigation state (from Prompt Examples page)
  useEffect(() => {
    if (location.state?.prefillPrompt) {
      setPrompt(location.state.prefillPrompt);
      // Clear the state to avoid re-filling on subsequent navigations
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // Fetch user collections when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchCollections();
    }
  }, [isAuthenticated, token]);

  const fetchCollections = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/collections`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCollections(response.data);
    } catch (error) {
      console.error('Failed to fetch collections:', error);
    }
  };

  const handleCollectionCreated = (newCollection) => {
    setCollections(prev => [newCollection, ...prev]);
  };
  
  // Get a random inspiring quote for preview
  const getRandomQuote = () => {
    return inspiringQuotes[Math.floor(Math.random() * inspiringQuotes.length)];
  };

  // Play completion chime
  const playCompletionChime = () => {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      
      const audioContext = new AudioContext();
      
      // Create ascending chime notes
      const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      
      notes.forEach((freq, i) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + i * 0.15);
        
        gainNode.gain.setValueAtTime(0, audioContext.currentTime + i * 0.15);
        gainNode.gain.linearRampToValueAtTime(0.12, audioContext.currentTime + i * 0.15 + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + i * 0.15 + 0.8);
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.start(audioContext.currentTime + i * 0.15);
        oscillator.stop(audioContext.currentTime + i * 0.15 + 0.8);
      });
      
      // Close audio context after sounds complete
      setTimeout(() => audioContext.close(), 2000);
    } catch (error) {
      // Audio not supported, silently fail
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    
    // Check limits before generating - use both server limits AND localStorage for anonymous
    if (!isAuthenticated) {
      if (needsSignup) {
        toast.error('Please sign up or sign in to continue generating fonts', {
          action: {
            label: 'Sign Up',
            onClick: () => navigate('/register')
          }
        });
        return;
      }
    }
    
    if (generationLimits) {
      if (!generationLimits.can_generate) {
        if (generationLimits.needs_signup) {
          toast.error('Please sign up or sign in to continue generating fonts');
          return;
        }
        if (generationLimits.needs_credits) {
          toast.error('Please purchase credits to continue generating fonts');
          return;
        }
      }
    }

    setLoading(true);
    try {
      const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
      const response = await axios.post(
        `${API_URL}/api/fonts/generate`,
        {
          prompt: prompt.trim(),
          typeface_category: typefaceCategory !== 'none' ? typefaceCategory : null,
          letter_space: letterSpace,
          decoration: decoration !== 'none' ? decoration : null,
          decoration_style: decoration !== 'none' ? decorationStyle : null,
        },
        config
      );
      const fontWithQuote = {
        ...response.data,
        preview_text: response.data.preview_text || getRandomQuote()
      };
      setGeneratedFont(fontWithQuote);
      
      // Update local tracking for anonymous users
      if (!isAuthenticated) {
        const newCount = localGenerationCount + 1;
        setLocalGenerationCount(newCount);
        localStorage.setItem('funtzguntz-anon-generations', newCount.toString());
      }
      
      // Refresh generation limits
      fetchGenerationLimits();
      
      // Play sacred completion chime
      playCompletionChime();
      
      toast.success('Font generated successfully!');
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.detail || 'Failed to generate font';
      
      // Handle specific limit errors
      if (status === 403) {
        // Anonymous user hit limit - also update localStorage
        const newCount = Math.max(localGenerationCount, 3);
        setLocalGenerationCount(newCount);
        localStorage.setItem('funtzguntz-anon-generations', newCount.toString());
        
        toast.error(message, {
          action: {
            label: 'Sign Up',
            onClick: () => navigate('/register')
          }
        });
      } else if (status === 402) {
        // Logged-in user needs credits
        toast.error(message, {
          action: {
            label: 'Buy Credits',
            onClick: () => navigate('/dashboard')
          }
        });
      } else {
        toast.error(message);
      }
      
      // Refresh limits after error
      fetchGenerationLimits();
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewDownloadPurchase = async (format) => {
    if (!isAuthenticated) {
      toast.error('Please log in to purchase');
      navigate('/login');
      return;
    }

    if (!generatedFont) return;

    setPurchasingPreview(format);
    try {
      const response = await axios.post(
        `${API_URL}/api/preview-download/checkout`,
        {
          font_id: generatedFont.id,
          format: format,
          border_style: borderStyle,
          border_color: borderColor,
          preview_text: previewText || generatedFont.preview_text,  // Use custom or default preview text
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
    } finally {
      setPurchasingPreview(null);
    }
  };

  const handleRegenerate = () => {
    setGeneratedFont(null);
    handleGenerate();
  };

  return (
    <div className="min-h-screen bg-[#0D0507]" data-testid="generator-page">
      <Header />
      
      {/* Sacred Generation Overlay */}
      <SacredGenerationOverlay isVisible={loading} />
      
      <main className="pt-24 pb-16 px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <div className="mb-12 text-center">
            <p className="mono-label mb-3">Font Generator</p>
            <h1 className="text-3xl md:text-4xl font-cinzel font-semibold text-[#F4EFE6]">
              Create your custom font
            </h1>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Panel - Controls */}
            <div className="lg:col-span-5">
              <div className="card-surface p-8 space-y-6 rounded-lg">
                {/* Typeface Category Selection */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-cinzel font-medium text-[#F4EFE6]">Typeface Category</label>
                    <button 
                      onClick={() => setShowCategoryInfo(!showCategoryInfo)}
                      className="text-[#C2B49A] hover:text-[#D4AF37] transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {typefaceCategories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setTypefaceCategory(cat.id)}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          typefaceCategory === cat.id
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                            : 'border-[#D4AF37]/20 hover:border-[#D4AF37]/40 text-[#C2B49A]'
                        }`}
                        title={cat.description}
                        data-testid={`category-${cat.id}`}
                      >
                        <span className="text-lg block mb-1">{cat.icon}</span>
                        <span className="text-xs font-cinzel font-medium">{cat.name}</span>
                      </button>
                    ))}
                  </div>
                  {showCategoryInfo && typefaceCategory !== 'none' && (
                    <div className="mt-3 p-3 bg-[#160A0D] rounded-lg border border-[#D4AF37]/20">
                      <p className="text-xs text-[#C2B49A] mb-1 font-serif">
                        {typefaceCategories.find(c => c.id === typefaceCategory)?.description}
                      </p>
                      <p className="text-xs text-[#C5A059] font-serif">
                        Examples: {typefaceCategories.find(c => c.id === typefaceCategory)?.examples}
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">Prompt</label>
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe your vision..."
                    className="input-terminal w-full min-h-[100px] resize-none rounded-lg"
                    data-testid="prompt-input"
                  />
                </div>

                {/* Letter-space Dropdown */}
                <div>
                  <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">Letter-space</label>
                  <Select value={letterSpace} onValueChange={setLetterSpace}>
                    <SelectTrigger 
                      className="input-terminal w-full rounded-lg"
                      data-testid="letterspace-select"
                    >
                      <SelectValue placeholder="Select letter spacing" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                      {letterSpaceOptions.map((option) => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Decoration Dropdown */}
                <div>
                  <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">Decoration</label>
                  <Select value={decoration} onValueChange={setDecoration}>
                    <SelectTrigger 
                      className="input-terminal w-full rounded-lg"
                      data-testid="decoration-select"
                    >
                      <SelectValue placeholder="Select decoration" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                      {decorationOptions.map((option) => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Decoration Style Dropdown - only show when decoration is selected */}
                {decoration !== 'none' && (
                  <div>
                    <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">Decoration Style</label>
                    <Select value={decorationStyle} onValueChange={setDecorationStyle}>
                      <SelectTrigger 
                        className="input-terminal w-full rounded-lg"
                        data-testid="decoration-style-select"
                      >
                        <SelectValue placeholder="Select decoration style" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                        {decorationStyleOptions.map((option) => (
                          <SelectItem 
                            key={option.value} 
                            value={option.value}
                            className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif"
                          >
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Generation Limits Display */}
                {(generationLimits || !isAuthenticated) && !generationLimits?.unlimited && (
                  <div className="p-4 bg-[#160A0D] rounded-lg border border-[#D4AF37]/20" data-testid="generation-limits-display">
                    {!isAuthenticated ? (
                      // Anonymous user limits - use max of server and localStorage
                      <div className="text-center">
                        {(() => {
                          const serverUsed = generationLimits?.generations_used || 0;
                          const effectiveUsed = Math.max(localGenerationCount, serverUsed);
                          const remaining = Math.max(0, 3 - effectiveUsed);
                          const limitReached = remaining === 0;
                          
                          return (
                            <>
                              <p className="text-sm text-[#C2B49A] font-serif mb-2">
                                Free Generations: <span className="text-[#D4AF37] font-cinzel font-bold">{remaining}</span> / 3
                              </p>
                              {limitReached && (
                                <p className="text-xs text-[#D4AF37] font-serif">
                                  Sign up for 7 more free generations!
                                </p>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    ) : (
                      // Logged-in user limits
                      <div className="text-center">
                        {generationLimits?.free_remaining > 0 ? (
                          <>
                            <p className="text-sm text-[#C2B49A] font-serif mb-2">
                              Free Generations: <span className="text-[#D4AF37] font-cinzel font-bold">{generationLimits.free_remaining}</span> / {generationLimits.free_limit || 7}
                            </p>
                            {generationLimits.credits > 0 && (
                              <p className="text-xs text-[#C2B49A] font-serif">
                                Plus {generationLimits.credits} credit{generationLimits.credits !== 1 ? 's' : ''} available
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-sm text-[#C2B49A] font-serif mb-2">
                            Credits: <span className="text-[#D4AF37] font-cinzel font-bold">{generationLimits?.credits || 0}</span>
                          </p>
                        )}
                        {generationLimits?.needs_credits && (
                          <p className="text-xs text-[#D4AF37] font-serif">
                            Purchase credits to continue generating
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Limit Exceeded - Sign Up Prompt */}
                {needsSignup && (
                  <div className="p-4 bg-[#722F37]/20 rounded-lg border border-[#722F37]/40" data-testid="signup-limit-prompt">
                    <div className="flex items-center gap-3 mb-3">
                      <Lock className="w-5 h-5 text-[#D4AF37]" />
                      <p className="text-sm font-cinzel font-semibold text-[#F4EFE6]">Generation Limit Reached</p>
                    </div>
                    <p className="text-sm text-[#C2B49A] font-serif mb-4">
                      You've used all 3 free generations. Sign up for a free account to get 7 more!
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => navigate('/register')}
                        className="flex-1 btn-primary rounded-lg"
                        data-testid="signup-prompt-btn"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        Sign Up Free
                      </Button>
                      <Button
                        onClick={() => navigate('/login')}
                        variant="outline"
                        className="flex-1 btn-secondary rounded-lg"
                        data-testid="signin-prompt-btn"
                      >
                        <LogIn className="w-4 h-4 mr-2" />
                        Sign In
                      </Button>
                    </div>
                  </div>
                )}

                {/* Limit Exceeded - Buy Credits Prompt */}
                {needsCredits && (
                  <div className="p-4 bg-[#722F37]/20 rounded-lg border border-[#722F37]/40" data-testid="buy-credits-limit-prompt">
                    <div className="flex items-center gap-3 mb-3">
                      <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                      <p className="text-sm font-cinzel font-semibold text-[#F4EFE6]">Credits Needed</p>
                    </div>
                    <p className="text-sm text-[#C2B49A] font-serif mb-4">
                      You've used all 7 free generations. Purchase credits to continue creating fonts.
                    </p>
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="w-full btn-primary rounded-lg"
                      data-testid="buy-credits-prompt-btn"
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Buy Credits
                    </Button>
                  </div>
                )}

                <Button
                  onClick={handleGenerate}
                  disabled={loading || !prompt.trim() || needsSignup || needsCredits}
                  className={`w-full py-6 text-base font-cinzel font-medium tracking-wider rounded-lg ${
                    loading || needsSignup || needsCredits
                      ? 'bg-[#722F37]/50 cursor-not-allowed' 
                      : 'btn-primary'
                  }`}
                  data-testid="generate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : needsSignup ? (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Sign Up to Continue
                    </>
                  ) : needsCredits ? (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Buy Credits to Continue
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate Font
                    </>
                  )}
                </Button>

                {/* Preview Text Input */}
                <div>
                  <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">Custom preview text</label>
                  <Input
                    value={previewText}
                    onChange={(e) => setPreviewText(e.target.value)}
                    placeholder="Type to preview..."
                    className="input-terminal w-full rounded-lg"
                    data-testid="preview-text-input"
                  />
                </div>

                {/* Border Style Dropdown */}
                <div>
                  <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">
                    Border for Preview Download
                    <span className="text-xs text-[#C5A059] ml-2">(High-Res 5000×5000px)</span>
                  </label>
                  <Select value={borderStyle} onValueChange={setBorderStyle}>
                    <SelectTrigger 
                      className="input-terminal w-full rounded-lg"
                      data-testid="border-select"
                    >
                      <SelectValue placeholder="Select a border style" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg max-h-[300px]">
                      {borderStyles.map((border) => (
                        <SelectItem 
                          key={border.value} 
                          value={border.value}
                          className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg py-3"
                        >
                          <div>
                            <span className="font-cinzel font-medium">{border.label}</span>
                            {border.description && (
                              <span className="block text-xs text-[#C2B49A] mt-0.5 font-serif">{border.description}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Border Color Dropdown */}
                {borderStyle !== 'none' && (
                  <div>
                    <label className="text-sm font-cinzel font-medium text-[#F4EFE6] mb-3 block">
                      Border Color
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={borderColor}
                        onChange={(e) => setBorderColor(e.target.value)}
                        className="w-12 h-12 rounded-lg border border-[#D4AF37]/25 cursor-pointer p-1 bg-[#160A0D]"
                        data-testid="border-color-picker"
                      />
                      <input
                        type="text"
                        value={borderColor}
                        onChange={(e) => setBorderColor(e.target.value)}
                        placeholder="#000000"
                        className="input-terminal flex-1 uppercase rounded-lg"
                        data-testid="border-color-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Preview */}
            <div className="lg:col-span-7">
              <div className="card-surface p-8 h-full flex flex-col rounded-lg">
                <div className="flex items-center justify-between mb-6">
                  <span className="mono-label">Preview</span>
                  {generatedFont && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRegenerate}
                      disabled={loading}
                      className="text-[#C2B49A] hover:text-[#D4AF37] hover:bg-[#220F13] rounded-lg"
                      data-testid="regenerate-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Regenerate
                    </Button>
                  )}
                </div>

                {/* Font Preview Area */}
                <div className="flex-1 flex flex-col">
                  {generatedFont ? (
                    <>
                      <FontPreview 
                        font={generatedFont} 
                        previewText={previewText}
                        borderStyle={borderStyle}
                        borderColor={borderColor}
                        showShareButtons={true}
                        className="mb-8"
                      />
                      
                      {/* Font Details */}
                      <div className="border-t border-[#D4AF37]/20 pt-6 space-y-4">
                        <div>
                          <h3 className="text-2xl font-cinzel font-bold mb-2 text-[#F4EFE6]">
                            {generatedFont.name}
                          </h3>
                          <p className="text-[#C2B49A] text-sm max-w-md font-serif">
                            {generatedFont.description}
                          </p>
                        </div>

                        {/* Style Properties */}
                        <div className="flex flex-wrap gap-2">
                          {generatedFont.style_properties && Object.entries(generatedFont.style_properties).map(([key, value]) => (
                            <span 
                              key={key}
                              className="text-xs font-mono px-3 py-1 bg-[#160A0D] border border-[#D4AF37]/20 text-[#C5A059] rounded-full"
                            >
                              {key}: {String(value)}
                            </span>
                          ))}
                        </div>

                        {/* Share & Save Row */}
                        <div className="flex items-center justify-between gap-4 mt-4">
                          <ShareButtons font={generatedFont} />
                          {isAuthenticated && (
                            <CollectionManager
                              fontId={generatedFont.id}
                              collections={collections}
                              onCollectionCreated={handleCollectionCreated}
                              compact={true}
                            />
                          )}
                        </div>

                        {/* Preview Download Section */}
                        <div className="mt-6 p-4 bg-[#160A0D] border border-[#D4AF37]/20 rounded-lg">
                          <div className="flex items-center gap-2 mb-3">
                            <ImageIcon className="w-4 h-4 text-[#D4AF37]" />
                            <span className="text-sm font-cinzel font-medium text-[#F4EFE6]">High-Res Preview Download</span>
                            <span className="text-xs text-[#D4AF37] font-cinzel font-bold ml-auto">$1.99</span>
                          </div>
                          <p className="text-xs text-[#C2B49A] mb-3 font-serif">
                            Download a stunning 5000×5000px preview image of your font
                          </p>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePreviewDownloadPurchase('jpg')}
                              disabled={purchasingPreview === 'jpg'}
                              className="flex-1 btn-secondary rounded-lg text-xs"
                              data-testid="download-jpg-btn"
                            >
                              {purchasingPreview === 'jpg' ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              JPG (5000×5000)
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePreviewDownloadPurchase('png')}
                              disabled={purchasingPreview === 'png'}
                              className="flex-1 btn-secondary rounded-lg text-xs"
                              data-testid="download-png-btn"
                            >
                              {purchasingPreview === 'png' ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Download className="w-3 h-3 mr-1" />
                              )}
                              PNG Transparent
                            </Button>
                          </div>
                        </div>

                        {/* Scripture Insight Section */}
                        <div className="mt-6">
                          <ScriptureInsight
                            meaning={generatedFont.scripture_meaning}
                            significance={generatedFont.scripture_significance}
                            previewText={generatedFont.preview_text}
                            onDownloadPdf={isAuthenticated ? () => downloadScripturePdf(
                              `/api/scripture-pdf/font/${generatedFont.id}?border_style=${encodeURIComponent(borderStyle)}&border_color=${encodeURIComponent(borderColor)}`,
                              token
                            ) : undefined}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      {/* Empty State Square Preview */}
                      <div className="aspect-square w-full max-w-[500px] mx-auto bg-[#160A0D] border-2 border-dashed border-[#D4AF37]/30 flex flex-col items-center justify-center rounded-lg mb-4">
                        <div className="w-16 h-16 flex items-center justify-center gothic-border rounded-lg mb-4">
                          <Type className="w-8 h-8 text-[#D4AF37]" />
                        </div>
                        <h3 className="text-lg font-cinzel font-semibold mb-2 text-[#C2B49A]">
                          No font generated yet
                        </h3>
                        <p className="text-sm text-[#C2B49A]/60 max-w-xs text-center px-4 font-serif">
                          Enter a prompt and click Generate to create your custom font
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
